import type { TCommercial, TSupplierRole } from "@/api";
import { getSupplierRoles } from "@/api";
import { useAdmin } from "@/hooks/admin";
import { Grid } from "@mui/material";
import {
  Edit,
  EmailField,
  FunctionField,
  SaveButton,
  SelectInput,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  Toolbar,
  TopToolbar,
  EditButton,
  email,
  required,
  useRecordContext,
} from "react-admin";
import useSWR from "swr";

const CommercialShowToolbar = () => {
  const { hasPermission } = useAdmin();
  if (!hasPermission("supplier.action.createSeller")) return null;
  return (
    <TopToolbar>
      <EditButton />
    </TopToolbar>
  );
};

function RoleNameDisplay({
  roleId,
  supplierId,
}: {
  roleId?: string;
  supplierId?: string;
}) {
  const { data: rolesData } = useSWR(
    supplierId ? ["supplier-roles-show", supplierId] : null,
    () => getSupplierRoles(supplierId!, { page: 0, size: 1000 }),
    { revalidateOnFocus: false }
  );
  const name =
    rolesData?.content?.find((r: TSupplierRole) => r.id === roleId)?.name ??
    "-";
  return <span>{name}</span>;
}

export const SupplierCommercialUserShow = () => (
  <Show actions={<CommercialShowToolbar />}>
    <SimpleShowLayout>
      <TextField source="firstName" />
      <TextField source="lastName" />
      <EmailField source="email" />
      <TextField source="phone" />
      <FunctionField
        label="Rol"
        render={(r: TCommercial) => (
          <RoleNameDisplay roleId={r.roleId} supplierId={r.supplierId} />
        )}
      />
    </SimpleShowLayout>
  </Show>
);

function CommercialUserRoleSelect() {
  const record = useRecordContext<TCommercial>();
  const { data: rolesData } = useSWR(
    record?.supplierId ? ["supplier-roles-edit", record.supplierId] : null,
    () => getSupplierRoles(record.supplierId, { page: 0, size: 1000 }),
    { revalidateOnFocus: false }
  );
  const choices =
    rolesData?.content?.map((role: TSupplierRole) => ({
      id: role.id,
      name: role.name,
    })) ?? [];

  return (
    <SelectInput
      source="roleId"
      choices={choices}
      validate={required()}
      fullWidth
      label="Rol"
    />
  );
}

const EditToolbar = () => (
  <Toolbar>
    <SaveButton />
  </Toolbar>
);

export const SupplierCommercialUserEdit = () => (
  <Edit
    mutationMode="pessimistic"
    transform={(
      data: TCommercial & { password?: string },
      { previousData }: { previousData?: TCommercial }
    ) => {
      const next = {
        ...previousData,
        ...data,
        supplierId: previousData?.supplierId ?? data.supplierId,
      };
      if (!next.password) {
        delete next.password;
      }
      return next;
    }}
  >
    <SimpleForm toolbar={<EditToolbar />} mode="onChange" warnWhenUnsavedChanges>
      <Grid container spacing={4}>
        <Grid item xs={6}>
          <TextInput source="firstName" validate={required()} fullWidth />
        </Grid>
        <Grid item xs={6}>
          <TextInput source="lastName" fullWidth />
        </Grid>
        <Grid item xs={6}>
          <TextInput
            source="email"
            validate={[required(), email()]}
            parse={(v: string) => v?.toLowerCase()}
            fullWidth
          />
        </Grid>
        <Grid item xs={6}>
          <CommercialUserRoleSelect />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            source="phone"
            validate={required()}
            fullWidth
            helperText="Número completo (igual que en el catálogo / API de usuarios)"
          />
        </Grid>
        <Grid item xs={12}>
          <TextInput
            source="password"
            type="password"
            fullWidth
            helperText="Dejar vacío para no cambiar la contraseña"
          />
        </Grid>
      </Grid>
    </SimpleForm>
  </Edit>
);
